// ── Interval habits ("every N days / weeks / months") ─────────────────────────
// Added 2026-10-15 (Jade). frequencyType "interval" with:
//   intervalEvery  N (days 2-30, weeks 2-12, months 2-12)
//   intervalUnit   "day" | "week" | "month"
//   intervalStart  "YYYY-MM-DD", stamped by cleanHabitForm when the habit is
//                  saved; only used when there is no habitStartDate.
//
// FIXED schedule, anchored to the start date (Jade's pick): every 3 days from
// Oct 1 is Oct 1, 4, 7, 10..., whatever was done. A missed day stays missed and
// never shifts the next one. That keeps "is it due on X" a pure function of the
// habit and the date, which every streak, score, tracker, widget, reminder and
// the server push already rely on.
//
// Months: the anchor's day of the month, or the month's last day when it is
// shorter (every 2 months from Jan 31 -> Mar 31, May 31; from Dec 31 -> Feb 28).
//
// MIRRORED in lib/habit-reminder-logic.js (isIntervalDueOn): the server push
// must agree with the phone about which days are due. habitInterval.test.js
// checks the two day by day. Change both together.

export const INTERVAL_UNITS = ["day", "week", "month"];
export const INTERVAL_RANGE = { day: [2, 30], week: [2, 12], month: [2, 12] };
export const INTERVAL_DEFAULT_EVERY = { day: 2, week: 2, month: 3 };

export function intervalUnitOf(h) {
  return INTERVAL_UNITS.includes(h && h.intervalUnit) ? h.intervalUnit : "day";
}

export function intervalEveryOf(h) {
  const unit = intervalUnitOf(h);
  const [lo, hi] = INTERVAL_RANGE[unit];
  const n = parseInt(h && h.intervalEvery, 10);
  if (!Number.isFinite(n)) return INTERVAL_DEFAULT_EVERY[unit];
  return Math.min(hi, Math.max(lo, n));
}

// The day the rhythm counts from: the habit's start date, else the day it was
// first saved as an interval habit. "" when neither is known (never due).
export function intervalAnchor(h) {
  return (h && (h.habitStartDate || h.intervalStart)) || "";
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parts(s) {
  const m = DATE_RE.exec(s || "");
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}
// Whole days from a to b, done in UTC so a daylight-saving change can't make
// a day 23 or 25 hours long.
function dayDiff(a, b) {
  return Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86400000);
}
function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

export function isIntervalDueOn(h, dateStr) {
  return isIntervalDueFrom(h, intervalAnchor(h), dateStr);
}

// Same rule from an explicit anchor. Planner tasks and events (Jade, 2026-10-16)
// repeat on an interval too; their anchor is the start date (task.startDate ||
// task.dueDate, event.date), not habitStartDate. Mirrored in lib/interval.js.
export function isIntervalDueFrom(h, anchorStr, dateStr) {
  const a = parts(anchorStr);
  const d = parts(dateStr);
  if (!a || !d) return false;
  const every = intervalEveryOf(h);
  const unit = intervalUnitOf(h);
  if (unit === "month") {
    const months = (d[0] - a[0]) * 12 + (d[1] - a[1]);
    if (months < 0 || months % every !== 0) return false;
    return d[2] === Math.min(a[2], daysInMonth(d[0], d[1]));
  }
  const diff = dayDiff(a, d);
  const step = unit === "week" ? every * 7 : every;
  return diff >= 0 && diff % step === 0;
}

// How many due days fall in [fromStr, toStr], inclusive. Used for targets
// (a 7-day week holds 3 or 4 of an every-2-days habit's due days).
export function intervalDueDaysBetween(h, fromStr, toStr) {
  const from = parts(fromStr), to = parts(toStr);
  if (!from || !to) return 0;
  let n = 0;
  const cur = new Date(Date.UTC(from[0], from[1] - 1, from[2]));
  const end = Date.UTC(to[0], to[1] - 1, to[2]);
  for (let i = 0; cur.getTime() <= end && i < 4000; i++) {
    const s = cur.toISOString().slice(0, 10);
    if (isIntervalDueOn(h, s)) n++;
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return n;
}

// Average due days per period of `days` length, for targets that have no
// fixed window (e.g. a 28-day period or a year). Rounded, at least 1.
export function intervalDuePerDays(h, days) {
  const unit = intervalUnitOf(h);
  const every = intervalEveryOf(h);
  const cycle = unit === "month" ? every * 30.4375 : unit === "week" ? every * 7 : every;
  return Math.max(1, Math.round(days / cycle));
}

const UNIT_WORD = { day: "days", week: "weeks", month: "months" };
// "Every 3 days", "Every 2 weeks", "Every 6 months".
export function intervalLabel(h) {
  return `Every ${intervalEveryOf(h)} ${UNIT_WORD[intervalUnitOf(h)]}`;
}
